import React from 'react'

import { PHASE_VIEWS } from './constants'

export const getPhaseView = phase => PHASE_VIEWS[phase] || PHASE_VIEWS.vision

export const getPhaseIndex = (phaseSteps, phase) => {
  const index = phaseSteps.findIndex(item => item.key === phase)
  return index === -1 ? 0 : index
}

export const getNextPhaseLabel = (phaseSteps, currentPhase) => {
  const index = getPhaseIndex(phaseSteps, currentPhase)
  const nextPhase = phaseSteps[index + 1]
  return nextPhase ? getPhaseView(nextPhase.key).label : 'Shot List'
}

export const getPhaseStatus = ({
  phaseSteps,
  currentPhase,
  phase,
  isFinalized
}) => {
  const currentIndex = getPhaseIndex(phaseSteps, currentPhase)
  const phaseIndex = getPhaseIndex(phaseSteps, phase)

  if (isFinalized || phaseIndex < currentIndex) return 'complete'
  if (phaseIndex === currentIndex) return 'active'
  return 'pending'
}

export const isPhaseSelectable = ({
  phaseSteps,
  currentPhase,
  phase,
  isFinalized
}) =>
  isFinalized ||
  getPhaseIndex(phaseSteps, phase) <= getPhaseIndex(phaseSteps, currentPhase)

export const getStatusLabel = status => {
  if (status === 'complete') return 'Done'
  if (status === 'active') return 'In Progress'
  return 'Pending'
}

export const getPhaseCount = (phaseData = {}, phase) => {
  const counts = phaseData.status?.counts || {}
  const countKey = phase === 'storyboard' ? 'panels' : phase
  const savedCount =
    phase === 'storyboard'
      ? phaseData.storyboard?.committedPanels
      : phaseData[phase]?.count

  return savedCount || counts[countKey] || 0
}

const getPhaseAssetList = (phaseData = {}, phase) => {
  const phaseAssets = phaseData[phase]?.generated
  if (Array.isArray(phaseAssets)) return phaseAssets
  if (Array.isArray(phaseAssets?.generated)) {
    return phaseAssets.generated
  }
  if (Array.isArray(phaseAssets?.assets)) {
    return phaseAssets.assets
  }

  return []
}

export const isReferenceAsset = asset => {
  const assetType = String(asset?.asset_type || asset?.assetType || '')
    .toLowerCase()
    .trim()

  return assetType === 'user_reference' || assetType === 'reference'
}

const getAssetIdentity = asset =>
  String(
    asset?.id ||
      asset?.image_url ||
      asset?.imageUrl ||
      asset?.thumbnail_url ||
      asset?.thumbnailUrl ||
      ''
  )

export const getGeneratedAssets = (phaseData = {}, phase) =>
  getPhaseAssetList(phaseData, phase).filter(asset => !isReferenceAsset(asset))

export const getReferenceAssets = (phaseData = {}, phase) => {
  const explicitReferences = phaseData[phase]?.referenceUploads || []
  const generatedReferences = getPhaseAssetList(phaseData, phase).filter(
    isReferenceAsset
  )
  const seen = {}

  return [...explicitReferences, ...generatedReferences].filter(asset => {
    const key = getAssetIdentity(asset)
    if (!key) return true
    if (seen[key]) return false
    seen[key] = true
    return true
  })
}

export const isStoryboardPlanApproved = (phaseData = {}) =>
  Boolean(
    phaseData.storyboard?.planApproved ||
      phaseData.storyboard?.committedPanels ||
      phaseData.status?.storyboardPlanApproved
  )

export const getStoryboardPlanPayload = plan => {
  if (!plan || typeof plan !== 'object') return {}
  return plan.plan || plan.storyboard_plan || plan.storyboardPlan || plan
}

export const getStoryboardPlanShots = plan => {
  const payload = getStoryboardPlanPayload(plan)
  const candidates = [
    payload.shots,
    payload.panels,
    payload.storyboard,
    payload.plan?.shots,
    payload.storyboard_plan?.shots
  ]

  return candidates.find(Array.isArray) || []
}

export const getStoryboardPlanCount = plan => {
  const payload = getStoryboardPlanPayload(plan)
  const shots = getStoryboardPlanShots(plan)
  return (
    payload.proposed_count ||
    payload.proposedCount ||
    payload.committed_panels ||
    payload.committedPanels ||
    shots.length ||
    0
  )
}

export const getStoryboardPlanRationale = plan => {
  const payload = getStoryboardPlanPayload(plan)
  return (
    payload.rationale ||
    payload.description ||
    payload.summary ||
    payload.notes ||
    ''
  )
}

export const formatPlanValue = value => {
  if (Array.isArray(value)) return value.filter(Boolean).join(', ')
  if (value === undefined || value === null) return ''
  return String(value)
}

export const formatCount = value => {
  const count = Number(value || 0)
  return Number.isFinite(count) ? count : 0
}

export const getFileExtension = fileName => {
  const parts = String(fileName || '').split('.')
  return parts.length > 1 ? parts.pop().toLowerCase() : ''
}

export const isAcceptedReferenceFile = (file, acceptedExtensions = []) => {
  const extension = getFileExtension(file?.name)
  return acceptedExtensions.map(item => item.toLowerCase()).includes(extension)
}

export const getReferenceKind = file => {
  if (String(file?.type || '').startsWith('video/')) return 'video'
  if (String(file?.type || '').startsWith('image/')) return 'image'

  const extension = getFileExtension(file?.name)
  return ['mp4', 'mov', 'webm'].includes(extension) ? 'video' : 'image'
}

export const formatFileSize = size => {
  const fileSize = Number(size || 0)
  if (!fileSize) return ''
  if (fileSize < 1024 * 1024) return `${Math.ceil(fileSize / 1024)} KB`
  return `${(fileSize / (1024 * 1024)).toFixed(1)} MB`
}

export const getAcceptedAttribute = acceptedExtensions =>
  (acceptedExtensions || []).map(ext => `.${ext}`).join(',')

export const isStoppableBusyAction = action =>
  /^(extract|verify|verify-all|generate|reference):/.test(String(action || '')) ||
  String(action || '') === 'storyboard:plan'

export const stripLooseMarkdownMarkers = value =>
  String(value || '')
    .replace(/\*\*/g, '')
    .replace(/\*/g, '')

export const splitLongParagraph = line => {
  const sourceText = String(line || '').trim()

  if (sourceText.length <= 420) return [sourceText]

  const text = stripLooseMarkdownMarkers(sourceText)

  const sentences = text.match(/[^.!?]+[.!?]+["')\]]*|[^.!?]+$/g)
  if (!sentences || sentences.length <= 1) return [text]

  const chunks = []
  let currentChunk = ''

  sentences.forEach(sentence => {
    const cleanSentence = sentence.trim()
    if (!cleanSentence) return

    const nextChunk = currentChunk
      ? `${currentChunk} ${cleanSentence}`
      : cleanSentence

    if (nextChunk.length > 360 && currentChunk) {
      chunks.push(currentChunk)
      currentChunk = cleanSentence
      return
    }

    currentChunk = nextChunk
  })

  if (currentChunk) chunks.push(currentChunk)

  return chunks.length ? chunks : [text]
}

export const revokePendingReferencePreviews = references => {
  (references || []).forEach(item => {
    if (item.previewUrl && window.URL?.revokeObjectURL) {
      window.URL.revokeObjectURL(item.previewUrl)
    }
  })
}

export const getFriendlyErrorMessage = error => {
  const text = String(error || '')

  if (/value too long|character varying\(200\)/i.test(text)) {
    return 'This phase hit a backend text limit. Shorten the vision, then try again.'
  }

  if (/all characters.*verified|verify all characters/i.test(text)) {
    return 'Approve every character before continuing to wardrobe.'
  }

  if (/creative statement/i.test(text) && /locked|set|required/i.test(text)) {
    return 'Save a concise vision before continuing.'
  }

  return text
}

export const renderInlineFormatting = text => {
  const parts = String(text || '').split(
    /(`[^`]+`|\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|\*[^*\n]+\*)/g
  )

  return parts.map((part, index) => {
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/)
    if (linkMatch) {
      const href = linkMatch[2]
      const isSafeHref = /^https?:\/\//i.test(href)

      return isSafeHref ? (
        <a
          href={href}
          key={`${part}-${index}`}
          rel="noopener noreferrer"
          target="_blank"
        >
          {linkMatch[1]}
        </a>
      ) : (
        linkMatch[1]
      )
    }

    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={`${part}-${index}`}>{part.slice(1, -1)}</code>
    }

    if (part.startsWith('**') && part.endsWith('**')) {
      const content = part.slice(2, -2)
      if (content.length > 140) return stripLooseMarkdownMarkers(content)
      return <strong key={`${part}-${index}`}>{content}</strong>
    }

    if (part.startsWith('*') && part.endsWith('*')) {
      const content = part.slice(1, -1)
      if (content.length > 140) return stripLooseMarkdownMarkers(content)
      return <em key={`${part}-${index}`}>{content}</em>
    }

    return stripLooseMarkdownMarkers(part)
  })
}

export const renderFormattedMessage = text => {
  const lines = String(text || '').split('\n')
  const blocks = []
  let listItems = []
  let listType = 'ul'
  let codeLines = []
  let isInCodeBlock = false

  const flushList = () => {
    if (!listItems.length) return

    const ListTag = listType
    blocks.push(
      <ListTag
        className="creative-direction-chat__content-list"
        key={`list-${blocks.length}`}
      >
        {listItems.map((item, index) => (
          <li key={`${item}-${index}`}>{renderInlineFormatting(item)}</li>
        ))}
      </ListTag>
    )
    listItems = []
    listType = 'ul'
  }

  const flushCodeBlock = () => {
    if (!codeLines.length) return

    blocks.push(
      <pre
        className="creative-direction-chat__code-block"
        key={`code-${blocks.length}`}
      >
        <code>{codeLines.join('\n')}</code>
      </pre>
    )
    codeLines = []
  }

  lines.forEach((line, index) => {
    const trimmedLine = line.trim()

    if (trimmedLine.startsWith('```')) {
      flushList()

      if (isInCodeBlock) {
        isInCodeBlock = false
        flushCodeBlock()
      } else {
        isInCodeBlock = true
        codeLines = []
      }

      return
    }

    if (isInCodeBlock) {
      codeLines.push(line)
      return
    }

    if (!trimmedLine) {
      flushList()
      return
    }

    const bulletMatch = trimmedLine.match(/^[-*]\s+(.+)$/)
    const numberedMatch = trimmedLine.match(/^\d+\.\s+(.+)$/)
    const headingMatch = trimmedLine.match(/^#{1,4}\s*(.+)$/)
    const quoteMatch = trimmedLine.match(/^>\s?(.+)$/)
    const dividerMatch = trimmedLine.match(/^(-{3,}|\*{3,}|_{3,})$/)

    if (headingMatch && headingMatch[1].length <= 120) {
      flushList()
      blocks.push(
        <h4 key={`heading-${index}`}>
          {renderInlineFormatting(headingMatch[1])}
        </h4>
      )
      return
    }

    if (headingMatch) {
      flushList()
      splitLongParagraph(headingMatch[1]).forEach((paragraph, paragraphIndex) => {
        blocks.push(
          <p key={`heading-paragraph-${index}-${paragraphIndex}`}>
            {renderInlineFormatting(paragraph)}
          </p>
        )
      })
      return
    }

    if (quoteMatch) {
      flushList()
      blocks.push(
        <blockquote key={`quote-${index}`}>
          {renderInlineFormatting(quoteMatch[1])}
        </blockquote>
      )
      return
    }

    if (dividerMatch) {
      flushList()
      blocks.push(<hr key={`divider-${index}`} />)
      return
    }

    if (bulletMatch) {
      if (listItems.length && listType !== 'ul') flushList()
      listType = 'ul'
      listItems.push(bulletMatch[1])
      return
    }

    if (numberedMatch) {
      if (listItems.length && listType !== 'ol') flushList()
      listType = 'ol'
      listItems.push(numberedMatch[1])
      return
    }

    flushList()
    splitLongParagraph(trimmedLine).forEach((paragraph, paragraphIndex) => {
      blocks.push(
        <p key={`line-${index}-${paragraphIndex}`}>
          {renderInlineFormatting(paragraph)}
        </p>
      )
    })
  })

  flushList()
  if (isInCodeBlock) {
    flushCodeBlock()
  }

  return blocks
}

export const getEntityKey = (entity, index) =>
  entity.id || entity.character_key || entity.name || index

export const getEntityApiId = entity =>
  entity?.id || entity?.entity_id || entity?.entityId || entity?.db_id || null

export const isEntityVerified = entity =>
  Boolean(entity?.user_verified || entity?.verified || entity?.is_verified)

export const getEntityDescription = entity =>
  entity.physical_description ||
  entity.description ||
  entity.role_in_story ||
  entity.character_arc ||
  entity.voice_description ||
  entity.scene_context ||
  'Ready for review'
